import { type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useParams } from 'react-router-dom'
import { useInView } from 'react-intersection-observer'
import LayoutContainer from 'components/Common/LayoutContainer'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Sheet from 'components/utils/Sheet'
import Group from 'components/utils/Group'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ErrorPage from 'components/Pages/ErrorPage'
import HeadData from 'components/Common/HeadData'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import AspectRatio from 'components/utils/AspectRatio'
import TextButton from 'LuxKit/Button/TextButton'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { formatMiddleDotSeparator } from 'LuxKit/intl/separators'
import { INTL_DMY_CASUAL_FORMAT, INTL_SHORT_HOUR_FORMAT_AM_PM, INTL_SHORT_TIME_FORMAT_AM_PM } from 'constants/dateFormats'
import type {
  Wedding,
  WeddingGift,
  WeddingMessage,
} from '../api/weddings'
import {
  getGreetingsContentBlock,
  getWeddingDayDetailsContentBlock,
  getRsvpFormContentBlock,
  getGalleryContentBlock,
  getPublicMessageBoardContentBlock,
  getFaqsContentBlock,
} from '../lib/utils'
import {
  getWeddingBySlug,
  getWeddingGifts,
  getWeddingMessages,
  WeddingStatus,
  WeddingTheme,
} from '../api/weddings'
import PaletteSheet from 'LuxKit/PaletteSheet'
import ModalContext from 'contexts/ModalContext'
import config from 'constants/config'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getCurrentUserId } from 'selectors/accountSelectors'
import styled from 'styled-components'
import { rem } from 'polished'
import LineUserPlusIcon from 'LuxKit/Icons/line/LineUserPlusIcon'
import clsx from 'clsx'
import WeddingCountdownTimer from 'weddings/components/WeddingCountdownTimer'
import WeddingPhotoCardsStack from 'weddings/components/WeddingPhotoCardsStack'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import Divider from 'LuxKit/Divider'
import WeddingDayDetail from 'weddings/components/WeddingDayDetail'
import LineUserCheckIcon from 'LuxKit/Icons/line/LineUserCheckIcon'
import LineClothesHangerIcon from 'LuxKit/Icons/line/LineClothesHangerIcon'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import WeddingGallery from 'weddings/components/WeddingGallery'
import WeddingGiftsList from 'weddings/components/WeddingGiftsList'
import WeddingGiftCardBanner from 'weddings/components/WeddingGiftCardBanner'
import WeddingMessagesList from 'weddings/components/WeddingMessagesList'
import WeddingWriteAMessageBanner from 'weddings/components/WeddingWriteAMessageBanner'
import WeddingWriteAMessageModal from 'weddings/components/WeddingWriteAMessageModal'
import WeddingFAQs from '../components/WeddingFAQs'
import { mediaQueryUp } from 'components/utils/breakpoint'
import WeddingPageHeader from 'weddings/components/WeddingPageHeader'
import WeddingContributeGiftModal from 'weddings/components/WeddingContributeGiftModal'
import type { ContributeGiftModalResult } from 'weddings/components/WeddingContributeGiftModal'
import WeddingRsvpModal from 'weddings/components/WeddingRsvpModal'
import WeddingVenueMapModal from 'weddings/components/WeddingVenueMapModal'
import { getPlaceBoundingRadius } from 'api/search'
import { scrollToSection } from 'weddings/lib/scrollUtils'
import { usePlaceById } from 'hooks/usePlace'
import { generateWeddingGiftCardItem } from 'lib/checkout/gift/cart'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { initialiseCheckout } from 'actions/CheckoutActions'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import zIndex from 'styles/tools/z-index'
import LineShareAltIcon from 'LuxKit/Icons/line/LineShareAltIcon'

const Root = styled.div`
  position: relative;

  .flex-1 {
    flex: 1;
  }
`

const MainPhotoSection = styled.div`
  margin: 0 auto;
  width: 100%;
`

const HeaderReadabilityOverlay = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    display: block;
    position: absolute;
    top: 0;
    inset-inline: 0;
    height: ${rem(320)};
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.4),
      transparent
    );
    pointer-events: none;
    z-index: ${zIndex.stickyHeader - 1};
  }
`

const HeadingSection = styled(Sheet)`
  border-start-end-radius: ${props => props.theme.borderRadius.S};
  border-start-start-radius: ${props => props.theme.borderRadius.S};
  position: relative;
  z-index: 1;

  &.with-image {
    margin-top: ${rem(-60)};
  }

  ${mediaQueryUp.desktop} {
    border-start-end-radius: ${props => props.theme.borderRadius.L};
    border-start-start-radius: ${props => props.theme.borderRadius.L};

    &.with-image {
      margin-top: ${rem(-180)};
    }
  }
`

const SectionWrapper = styled(Group)`
  padding: ${rem(28)} 0;
  width: 100%;

  ${mediaQueryUp.desktop} {
    padding: ${rem(64)} 0;
  }
`

const FloatingRequestButton = styled(TextButton)`
  position: fixed;
  bottom: ${rem(24)};
  inset-inline-end: ${rem(16)};
  z-index: ${zIndex.stickyFooter};
  box-shadow: ${props => props.theme.shadow.bottom.medium};

  ${mediaQueryUp.desktop} {
    bottom: ${rem(40)};
    inset-inline-end: ${rem(40)};
  }
`

function getPaletteType(theme: WeddingTheme): App.Design.PaletteType {
  return theme === WeddingTheme.DARK ? 'inverse' : 'default'
}

const SECTION_IDS = {
  GREETING: 'wedding-greeting',
  WEDDING_DAY: 'wedding-day',
  GALLERY: 'wedding-gallery',
  GIFTS: 'wedding-gifts',
  MESSAGES: 'wedding-messages',
  FAQS: 'wedding-faqs',
} as const

const MESSAGE_LIMIT_RECORDS = 100

interface RouteParams {
  regionCode: string
  slug: string
}

function WeddingPage() {
  const { regionCode = '', slug = '' } = useParams<keyof RouteParams>()
  const intl = useIntl()
  const currentUserId = useAppSelector(getCurrentUserId)
  const [wedding, setWedding] = useState<Wedding | null>(null)
  const [gifts, setGifts] = useState<Array<WeddingGift>>([])
  const [messages, setMessages] = useState<Array<WeddingMessage>>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error & { status?: number } | null>(null)
  const [_activeSection, setActiveSection] = useState<string | undefined>(undefined)
  const [isOwner, setIsOwner] = useState(false)

  const showModal = useContext(ModalContext)
  const dispatch = useAppDispatch()

  const defaultCurrency = useAppSelector(state => state.geo.currentCurrency)

  const { ref: mainPhotoRef, inView: mainPhotoInView } = useInView({ threshold: 0.3 })
  const { ref: _greetingRef, inView: greetingInView } = useInView({ threshold: 0.3 })
  const { ref: _giftsRef, inView: giftsInView } = useInView({ threshold: 0.3 })
  const { ref: _faqsRef, inView: faqsInView } = useInView({ threshold: 0.3 })

  useEffect(() => {
    if (greetingInView) setActiveSection(SECTION_IDS.GREETING)
    else if (giftsInView) setActiveSection(SECTION_IDS.GIFTS)
    else if (faqsInView) setActiveSection(SECTION_IDS.FAQS)
  }, [greetingInView, giftsInView, faqsInView])

  const fetchWeddingData = useCallback(async() => {
    try {
      setLoading(true)
      setError(null)
      setIsOwner(false)

      const weddingData = await getWeddingBySlug(slug)

      const isDraft = weddingData.status === WeddingStatus.DRAFT
      const isArchived = weddingData.status === WeddingStatus.ARCHIVED
      const ownerMatch = !!(currentUserId && weddingData.createdByUserId === currentUserId)

      const isAccessDenied = isArchived || (isDraft && !ownerMatch)

      if (isAccessDenied) {
        const accessError = new Error(intl.formatMessage({
          defaultMessage: 'This wedding page is not available.',
          id: 'pGGDC6nO6VOm8tYUHWxM',
          description: 'Error message shown when wedding page access is denied.',
        })) as Error & { status?: number }
        accessError.status = 404
        setError(accessError)
        setLoading(false)
        return
      }

      setIsOwner(ownerMatch)

      setWedding(weddingData)

      const [giftsData, messagesData] = await Promise.all([
        getWeddingGifts(weddingData.id),
        getWeddingMessages(weddingData.id, { limit: MESSAGE_LIMIT_RECORDS }),
      ])

      setGifts(giftsData)
      setMessages(messagesData.data)
    } catch(err) {
      const fetchError = err instanceof Error ? err : new Error(intl.formatMessage({
        defaultMessage: 'Failed to load wedding page',
        id: 'uh1CKH_XRpKCUYQ0iz3i',
        description: 'Error message when wedding page data fails to load.',
      }))
      if (!('status' in fetchError)) {
        (fetchError as Error & { status?: number }).status = 404
      }
      setError(fetchError as Error & { status?: number })
    } finally {
      setLoading(false)
    }
  }, [slug, currentUserId, intl])

  useEffect(() => {
    fetchWeddingData()
  }, [fetchWeddingData])

  const handleMessageSent = useCallback(async() => {
    if (!wedding) return
    try {
      const messagesData = await getWeddingMessages(wedding.id, { limit: MESSAGE_LIMIT_RECORDS })
      setMessages(messagesData.data)
    } catch {
      // silently ignore refresh failure — message was already sent
    }
  }, [wedding])

  const handleWriteMessage = useCallback(() => {
    if (!wedding) return
    showModal(
      <WeddingWriteAMessageModal
        weddingId={wedding.id}
        coupleFirstName={wedding.partnerOneFirstName}
        coupleSecondName={wedding.partnerTwoFirstName}
        onMessageSent={handleMessageSent}
      />,
    )
  }, [showModal, wedding, handleMessageSent])

  const showContributeGiftModal = useCallback(async({ gift, currency, amount, isFlexibleAmount }: { gift?: WeddingGift, currency: string, amount?: number, isFlexibleAmount?: boolean }) => {
    if (!wedding) return
    const result = await showModal<ContributeGiftModalResult>(
      <WeddingContributeGiftModal gift={gift} isFlexibleAmount={isFlexibleAmount} wedding={wedding} currency={currency} amount={amount} />,
    )
    if (result) {
      const item = generateWeddingGiftCardItem(wedding, result.gift, result.amount, intl, result)
      dispatch(initialiseCheckout([item]))
      dispatch(navigateToCheckout())
    }
  }, [showModal, wedding, dispatch, intl])

  const handleContributeGift = useCallback(async(gift: WeddingGift) => {
    showContributeGiftModal({ gift, currency: gift.currency, isFlexibleAmount: true })
  }, [showContributeGiftModal])

  const handleGiftCardBannerClick = useCallback(async(amount: number) => {
    const currency = gifts[0]?.currency ?? defaultCurrency
    showContributeGiftModal({ currency, amount })
  }, [showContributeGiftModal, gifts, defaultCurrency])

  const {
    greetingContent,
    weddingDayDetailsContent,
    rsvpFormContent,
    galleryContent,
    publicMessageBoardContent,
    faqsContent,
  } = useMemo(() => ({
    greetingContent: getGreetingsContentBlock(wedding?.contentBlocks),
    weddingDayDetailsContent: getWeddingDayDetailsContentBlock(wedding?.contentBlocks),
    rsvpFormContent: getRsvpFormContentBlock(wedding?.contentBlocks),
    galleryContent: getGalleryContentBlock(wedding?.contentBlocks),
    publicMessageBoardContent: getPublicMessageBoardContentBlock(wedding?.contentBlocks),
    faqsContent: getFaqsContentBlock(wedding?.contentBlocks),
  }), [wedding])

  const greetingMessage = greetingContent?.content?.message
  const greetingPhotoIds = useMemo(
    () => [...(greetingContent?.content?.photos ?? [])]
      .sort((a, b) => a.position - b.position)
      .map(p => p.imageId),
    [greetingContent],
  )

  const weddingDayContent = weddingDayDetailsContent?.content
  const faqItems = faqsContent?.content?.items ?? []

  const galleryImages = useMemo(() => {
    const photos = galleryContent?.content?.photos
    if (!photos?.length) return []
    return [...photos]
      .sort((a, b) => a.position - b.position)
      .map(p => ({ id: p.imageId, description: '' }))
  }, [galleryContent])

  const hasRsvpForm = !!rsvpFormContent
  const hasPublicMessageBoard = !!publicMessageBoardContent

  const handleRsvp = useCallback(async() => {
    if (!wedding || !rsvpFormContent) return
    const result = await showModal<boolean>(<WeddingRsvpModal wedding={wedding} />)

    if (result) {
      showSnackbar(
        intl.formatMessage(
          { defaultMessage: 'Would you like to gift {partnerOneFirstName} & {partnerTwoFirstName}?', id: 'x9knrD06jtYjhidme7Hz', description: 'Snackbar message shown after a guest confirms wedding attendance.' },
          { partnerOneFirstName: wedding.partnerOneFirstName, partnerTwoFirstName: wedding.partnerTwoFirstName },
        ),
        'success',
        {
          heading: intl.formatMessage({ defaultMessage: "You're on the list!", id: 'hC4BmDrEGmGdCuxvP_jR', description: 'Snackbar heading shown after a guest confirms wedding attendance.' }),
          action: { label: intl.formatMessage({ defaultMessage: 'Gift', id: 'Qr9t0J9fSVDQBKyDXbrH', description: 'Snackbar action label linking to the wedding gifts section.' }), onAction: () => scrollToSection(SECTION_IDS.GIFTS) },
        },
      )
    }
  }, [showModal, wedding, rsvpFormContent, intl])

  const hasWeddingCoords = Number.isFinite(wedding?.venueLatitude) && Number.isFinite(wedding?.venueLongitude)
  const venuePlace = usePlaceById(wedding?.venuePlaceId ?? '', hasWeddingCoords)
  const [venueCoords, setVenueCoords] = useState<{ latitude: number; longitude: number } | undefined>()

  useEffect(() => {
    if (hasWeddingCoords) {
      setVenueCoords({ latitude: wedding!.venueLatitude!, longitude: wedding!.venueLongitude! })
      return
    }

    if (!venuePlace?.id) return
    getPlaceBoundingRadius(venuePlace.id)
      .then(place => {
        const centre = place.boundingRadius?.centre
        if (centre) setVenueCoords({ latitude: centre.latitude, longitude: centre.longitude })
      })
      .catch(() => { })
  }, [venuePlace?.id, wedding, hasWeddingCoords])

  const handleDirections = useCallback(() => {
    if (!venueCoords) return
    showModal(<WeddingVenueMapModal
      latitude={venueCoords.latitude}
      longitude={venueCoords.longitude}
      venueName={wedding?.title ?? undefined}
    />)
  }, [showModal, venueCoords, wedding?.title])

  const weddingTitle = useMemo(() => {
    if (!wedding) return ''
    if (wedding.title) return wedding.title
    return intl.formatMessage(
      {
        id: 'N12CDs_fngp-QEEOCrcM',
        defaultMessage: '{partnerOneName} & {partnerTwoName}\'s Wedding',
        description: 'Display name for a wedding couple shown in wedding page sharing content',
      },
      {
        partnerOneName: wedding.partnerOneFirstName,
        partnerTwoName: wedding.partnerTwoFirstName,
      },
    )
  }, [wedding, intl])

  const mappedMessages = useMemo(
    () => messages.map(m => ({
      id: m.id,
      authorName: m.authorLastName
        ? `${m.authorFirstName} ${m.authorLastName[0]}.`
        : m.authorFirstName,
      content: m.content,
      date: m.createdAt,
    })),
    [messages],
  )

  const navigationItems = useMemo(() => {
    if (!wedding) return []

    const items: Array<{ id: string; label: string }> = []

    if (greetingContent) {
      items.push({ id: SECTION_IDS.GREETING, label: intl.formatMessage({ defaultMessage: 'Greetings', id: 'EwDUFTuvAN94QwOVGErO', description: 'Wedding page navigation label for the greeting section.' }) })
    }
    if (weddingDayDetailsContent) {
      items.push({ id: SECTION_IDS.WEDDING_DAY, label: intl.formatMessage({ defaultMessage: 'Wedding day', id: 'nGy-OIIAXKdq07cfTcvJ', description: 'Wedding page navigation label for the wedding day details section.' }) })
    }
    if (galleryContent) {
      items.push({ id: SECTION_IDS.GALLERY, label: intl.formatMessage({ defaultMessage: 'Gallery', id: 'bO5bJirgLe7CbooNXBOE', description: 'Wedding page navigation label for the photo gallery section.' }) })
    }
    items.push({ id: SECTION_IDS.GIFTS, label: intl.formatMessage({ defaultMessage: 'Gifts', id: 'R432iu4TB_l0zjDkJgQN', description: 'Wedding page navigation label for the gifts section.' }) })
    if (hasPublicMessageBoard) {
      items.push({ id: SECTION_IDS.MESSAGES, label: intl.formatMessage({ defaultMessage: 'Messages', id: 'XtDBwoNHTz9PV_1HIM1K', description: 'Wedding page navigation label for the public messages section.' }) })
    }
    if (faqsContent) {
      items.push({ id: SECTION_IDS.FAQS, label: intl.formatMessage({ defaultMessage: 'FAQs', id: 'tGNpKpCTKW7vi3bZuIHM', description: 'Wedding page navigation label for the frequently asked questions section.' }) })
    }

    return items
  }, [wedding, greetingContent, weddingDayDetailsContent, galleryContent, faqsContent, hasPublicMessageBoard, intl])

  const handleShare = useCallback(async() => {
    if (!wedding) return
    const url = `${config.SCHEME}://${config.CUSTOMER_PORTAL_HOST}/weddings/${wedding.slug}`

    if (navigator.share) {
      await navigator.share({ title: weddingTitle, url })
    } else {
      await navigator.clipboard.writeText(url)
      showSnackbar(intl.formatMessage({ defaultMessage: 'Link copied to clipboard', id: 'NYo3Gu3cwgH4Y8h1qZR-', description: 'Snackbar message after copying a wedding page link to the clipboard.' }), 'success')
    }
  }, [wedding, weddingTitle, intl])

  const rsvpButton = useMemo(() => {
    if (!handleRsvp) return
    return {
      label: intl.formatMessage({ defaultMessage: 'RSVP', id: 'YxQ8SusbBmUX3Cgpfw-9', description: 'Label for the RSVP button in the shared header on a wedding page.' }),
      icon: <LineUserPlusIcon />,
      onClick: handleRsvp,
    }
  }, [handleRsvp, intl])

  const shareButton = useMemo(() => ({
    label: intl.formatMessage({ defaultMessage: 'Share', id: 'pqcqgvGJ-G9ZUqX_7z1q', description: 'Label for the Share button in the shared header on a wedding page.' }),
    icon: <LineShareAltIcon />,
    onClick: handleShare,
  }), [handleShare, intl])

  if (loading) {
    return <LoadingIndicator />
  }

  if (error || !wedding) {
    const errorToShow = error || (new Error(intl.formatMessage({
      defaultMessage: 'Wedding not found',
      id: 'sF3nObT3yJPo2bwmIIlj',
      description: 'Error message when the wedding page cannot be found.',
    })) as Error & { status?: number })
    if (!errorToShow.status && !wedding) {
      errorToShow.status = 404
    }
    return <ErrorPage error={errorToShow} />
  }

  const paletteType = getPaletteType(wedding.theme)
  const weddingFallbackTitle = intl.formatMessage({
    defaultMessage: 'Wedding',
    id: '77rQYfl_fboftuYXUVLi',
    description: 'Fallback page title when a wedding has no custom title.',
  })

  return (
    <Root>
      <HeadData
        title={weddingTitle || weddingFallbackTitle}
        description={greetingMessage || undefined}
      />

      <PaletteSheet paletteType={paletteType}>
        {/* Page header */}
        <WeddingPageHeader
          isPastHero={!mainPhotoInView}
          navigationItems={navigationItems}
          primaryButtonLabel={rsvpButton?.label}
          primaryButtonStartIcon={rsvpButton?.icon}
          primaryButtonClick={rsvpButton?.onClick}
          secondaryButtonLabel={shareButton.label}
          secondaryButtonStartIcon={shareButton.icon}
          secondaryButtonClick={shareButton.onClick}
        />

        {wedding.mainImageId && <HeaderReadabilityOverlay aria-hidden="true" role="presentation" />}

        {/* Main Photo */}
        {wedding.mainImageId && (
          <MainPhotoSection ref={mainPhotoRef}>
            <AspectRatio
              ratio="31:19"
              tabletRatio="180:74"
            >
              <ResponsiveImage
                id={wedding.mainImageId}
                alt={weddingTitle || weddingFallbackTitle}
                quality="good"
                objectFit="cover"
                fit="center"
              />
            </AspectRatio>
          </MainPhotoSection>
        )}

        {/* Title, Date and Countdown Timer */}
        <LayoutContainer variant="large">
          <HeadingSection
            padding="20 32 0 32"
            desktopPadding="40 20 0 20"
            background="normal-background"
            className={clsx({ 'with-image': !!wedding.mainImageId })}
          >
            <Group gap={28} direction="vertical" horizontalAlign="center">
              <Group direction="vertical" gap={8} horizontalAlign="center">
                {weddingTitle && (
                  <Heading variant="heading1alt" align="center" as="h1">
                    {weddingTitle}
                  </Heading>
                )}
                <Group direction="horizontal" gap={4} horizontalAlign="center" verticalAlign="center">
                  {wedding.eventStartDate && (
                    <BodyText variant="medium" align="center">
                      {new Date(wedding.eventStartDate).getMinutes() == 0 ?
                          intlDateFormatter(new Date(wedding.eventStartDate), INTL_SHORT_HOUR_FORMAT_AM_PM, intl.locale) :
                          intlDateFormatter(new Date(wedding.eventStartDate), INTL_SHORT_TIME_FORMAT_AM_PM, intl.locale)}
                      {formatMiddleDotSeparator(intl)}
                      {intlDateFormatter(new Date(wedding.eventStartDate), INTL_DMY_CASUAL_FORMAT, intl.locale)}
                    </BodyText>
                  )}
                  {wedding.eventStartDate && wedding.venueAddress && (
                    <BodyText variant="medium" align="center">
                      {formatMiddleDotSeparator(intl)}
                    </BodyText>
                  )}
                  {wedding.venueAddress && (
                    <BodyText variant="medium" align="center" underline>
                      {wedding.venueAddress}
                    </BodyText>
                  )}
                </Group>
              </Group>
              <WeddingCountdownTimer startDate={wedding.eventStartDate} />
            </Group>
          </HeadingSection>
        </LayoutContainer>

        {/* Content Sections */}
        <Sheet padding="24 0" tabletPadding="40 0" background="normal-background">
          <LayoutContainer variant="large">
            <Group direction="vertical" gap={0} horizontalAlign="center">
              {/* Greeting Section */}
              {greetingContent && (
                <SectionWrapper direction="vertical" desktopDirection="horizontal" gap={64} horizontalAlign="space-between" id={SECTION_IDS.GREETING}>
                  <WeddingPhotoCardsStack photoIds={greetingPhotoIds} />
                  <Group direction="vertical" gap={20} className="flex-1">
                    <Group direction="vertical" horizontalAlign="center" desktopHorizontalAlign="start" gap={8}>
                      <Caption variant="small" weight="bold" format="uppercase" colour="light-foreground-alt">
                        {intl.formatMessage({ defaultMessage: 'Greeting', id: 'GguWTk2sxGAFqFBH5hjs', description: 'Section eyebrow label for the greeting section on a wedding page.' })}
                      </Caption>
                      <Heading variant="heading2alt" wrap="word-wrap" align="center">
                        {intl.formatMessage(
                          { defaultMessage: 'Celebrate <regular>with us</regular>', id: 'pOg9qsMZb6-97rZ6i4LA', description: 'Heading for the greeting section on a wedding page.' },
                          { regular: (chunks: ReactNode) => <Heading variant="heading2" wrap="word-wrap" as="span">{chunks}</Heading> },
                        )}
                      </Heading>
                    </Group>
                    <BodyText variant="large" wrap="pre-wrap" colour="light-foreground-alt">
                      {greetingMessage}
                    </BodyText>
                  </Group>
                </SectionWrapper>
              )}

              {/* Wedding Day Section */}
              {weddingDayContent && (
                <SectionWrapper direction="vertical" gap={20} desktopGap={40} horizontalAlign="center" id={SECTION_IDS.WEDDING_DAY}>
                  <Group direction="vertical" gap={8} horizontalAlign="center">
                    <Caption variant="small" weight="bold" format="uppercase" colour="light-foreground-alt">
                      {intl.formatMessage({ defaultMessage: 'Wedding Day', id: 'tugEcRPgdzRgjFPjUW_-', description: 'Section eyebrow label for the wedding day details section.' })}
                    </Caption>
                    <Heading variant="heading2alt" wrap="word-wrap" align="center">
                      {intl.formatMessage(
                        { defaultMessage: 'Everything <regular>you need to know</regular>', id: 'XDEkZqGDIBGVFdYlZ4nO', description: 'Heading for the wedding day details section on a public wedding page.' },
                        { regular: (chunks: ReactNode) => <Heading variant="heading2" wrap="word-wrap" as="span">{chunks}</Heading> },
                      )}
                    </Heading>
                  </Group>
                  <Group direction="vertical" desktopDirection="horizontal" gap={8} desktopGap={40} horizontalAlign="space-between">
                    <WeddingDayDetail
                      icon={<LineMapMarkerIcon size="L" />}
                      detailName={intl.formatMessage({ defaultMessage: 'Ceremony', id: 'CnaRjPLCx_9CCn_YKKcz', description: 'Label for ceremony details shown on the public wedding page.' })}
                      title={weddingDayContent.ceremony.venueName}
                      subtitle={weddingDayContent.ceremony.startTime}
                      note={weddingDayContent.ceremony.note}
                    />
                    <Divider kind="primary" direction="horizontal" desktopDirection="vertical" />
                    <WeddingDayDetail
                      icon={<LineUserCheckIcon size="L" />}
                      detailName={intl.formatMessage({ defaultMessage: 'Reception', id: 'rbRnfYUNsdZywiQnR6ip', description: 'Label for reception details shown on the public wedding page.' })}
                      title={weddingDayContent.reception.venueName}
                      subtitle={weddingDayContent.reception.startTime}
                      note={weddingDayContent.reception.note}
                    />
                    <Divider kind="primary" direction="horizontal" desktopDirection="vertical" />
                    <WeddingDayDetail
                      icon={<LineClothesHangerIcon size="L" />}
                      detailName={intl.formatMessage({ defaultMessage: 'Dress Code', id: 'vF2FIqSj2XFFCvI82DbI', description: 'Label for dress code details shown on the public wedding page.' })}
                      title={weddingDayContent.dressCode.code}
                      subtitle={weddingDayContent.dressCode.colourPalette}
                      note={weddingDayContent.dressCode.note}
                    />
                  </Group>
                  <Group direction="horizontal" gap={12} horizontalAlign="stretch" tabletHorizontalAlign="center" fullWidth>
                    {hasRsvpForm && (
                      <TextButton kind="primary" size="large" startIcon={<LineUserPlusIcon />} onClick={handleRsvp}>
                        {intl.formatMessage({ defaultMessage: 'RSVP', id: 'WYZ6aQ784wyB8MGcpnod', description: 'Button label for opening the RSVP modal from the wedding day section.' })}
                      </TextButton>
                    )}
                    {!!venueCoords && (
                      <TextButton
                        kind="secondary"
                        size="large"
                        variant="dark"
                        startIcon={<LineMapIcon />}
                        onClick={handleDirections}
                      >
                        {intl.formatMessage({ defaultMessage: 'Get directions', id: 'bh77j4KFis8He1isfMXd', description: 'Button label for opening directions to the wedding venue.' })}
                      </TextButton>
                    )}
                  </Group>
                </SectionWrapper>
              )}

              {/* Gallery Section */}
              {galleryContent && (
                <SectionWrapper direction="vertical" gap={20} desktopGap={40} horizontalAlign="center" id={SECTION_IDS.GALLERY}>
                  <Group direction="vertical" gap={8} horizontalAlign="center">
                    <Caption variant="small" weight="bold" format="uppercase" colour="light-foreground-alt">
                      {intl.formatMessage({ defaultMessage: 'Gallery', id: '3-6cqVlanschZxNiOdEd', description: 'Section eyebrow label for the wedding photo gallery.' })}
                    </Caption>
                    <Heading variant="heading2" wrap="word-wrap" align="center">
                      {intl.formatMessage(
                        { defaultMessage: 'Our <alt>moments</alt>', id: 'tBY_VdZEzVW0zbOIgRVi', description: 'Heading for the wedding photo gallery section on a public wedding page.' },
                        { alt: (chunks: ReactNode) => <Heading variant="heading2alt" wrap="word-wrap" as="span">{chunks}</Heading> },
                      )}
                    </Heading>
                  </Group>
                  <WeddingGallery images={galleryImages} />
                </SectionWrapper>
              )}

              {/* Gifts Section */}
              {gifts.length > 0 && (
                <SectionWrapper direction="vertical" gap={20} desktopGap={40} horizontalAlign="center" id={SECTION_IDS.GIFTS}>
                  <Group direction="vertical" gap={8} horizontalAlign="center">
                    <Caption variant="small" weight="bold" format="uppercase" colour="light-foreground-alt">
                      {intl.formatMessage({ defaultMessage: 'Gifts', id: '7MFBLGd5UNAAEmbveNM3', description: 'Section eyebrow label for the wedding gifts section.' })}
                    </Caption>
                    <Heading variant="heading2" wrap="word-wrap" align="center">
                      {intl.formatMessage(
                        { defaultMessage: 'Your presence is the <alt>greatest gift</alt>', id: 'ecezVln02nlW932tivAz', description: 'Heading for the public wedding gifts section on a wedding page.' },
                        { alt: (chunks: ReactNode) => <Heading variant="heading2alt" wrap="word-wrap" as="span">{chunks}</Heading> },
                      )}
                    </Heading>
                    <BodyText variant="large" wrap="word-wrap" align="center">
                      {intl.formatMessage({ defaultMessage: 'If you’d like to contribute, you can help us create meaningful experiences, travel, and a home together.', id: 'Bn72Cnege9tknu8bv38N', description: 'Body copy introducing the gifts section on a wedding page.' })}
                    </BodyText>
                  </Group>
                  <WeddingGiftsList gifts={gifts} onContributeClick={handleContributeGift} />
                  <WeddingGiftCardBanner onGiftClick={handleGiftCardBannerClick} />
                </SectionWrapper>
              )}

              {/* Messages Section */}
              {hasPublicMessageBoard && (
                <SectionWrapper direction="vertical" gap={40} horizontalAlign="center" id={SECTION_IDS.MESSAGES}>
                  <Group direction="vertical" gap={8} horizontalAlign="center">
                    <Caption variant="small" weight="bold" format="uppercase" colour="light-foreground-alt">
                      {intl.formatMessage({ defaultMessage: 'Messages', id: 'IdIE1XYLtEJTpBfUT0Wi', description: 'Section eyebrow label for the wedding messages section.' })}
                    </Caption>
                    <Heading variant="heading2" wrap="word-wrap" align="center">
                      {intl.formatMessage(
                        { defaultMessage: 'Notes from the people <alt>we love</alt>', id: '1SwqM-E_ut3hyYwbjqk4', description: 'Heading for the public wedding messages section on a wedding page.' },
                        { alt: (chunks: ReactNode) => <Heading variant="heading2alt" wrap="word-wrap" as="span">{chunks}</Heading> },
                      )}
                    </Heading>
                  </Group>
                  {!!mappedMessages.length && <WeddingMessagesList messages={mappedMessages} />}
                  <WeddingWriteAMessageBanner
                    onWriteMessage={handleWriteMessage}
                    hasMessages={!!mappedMessages.length}
                    partnerOneName={wedding.partnerOneFirstName}
                    partnerTwoName={wedding.partnerTwoFirstName}
                  />
                </SectionWrapper>
              )}

              {/* FAQs Section */}
              {faqsContent && (
                <LayoutContainer variant="medium">
                  <SectionWrapper direction="vertical" gap={20} desktopGap={40} horizontalAlign="center" id={SECTION_IDS.FAQS}>
                    <Group direction="vertical" gap={8} horizontalAlign="center">
                      <Heading variant="heading2">
                        {intl.formatMessage({ defaultMessage: 'FAQs', id: 'wcKyLFFanltwFXcuI0CX', description: 'Heading for the frequently asked questions section on a wedding page.' })}
                      </Heading>
                    </Group>
                    <WeddingFAQs qaItems={faqItems} />
                  </SectionWrapper>
                </LayoutContainer>
              )}

            </Group>
          </LayoutContainer>
        </Sheet>
      </PaletteSheet>

      {!isOwner && (
        <FloatingRequestButton
          kind="primary"
          variant="dark"
          size="large"
          to={`/${regionCode}/weddings/create`}
        >
          {intl.formatMessage({ defaultMessage: 'Create your wedding page', id: 'waqlDuvmHrRCKa-6jqhT', description: 'Floating button label inviting guests to create their own wedding page.' })}
        </FloatingRequestButton>
      )}
    </Root>
  )
}

export default WeddingPage
